import React, { FunctionComponent, useEffect, useState } from 'react'
import { Alert, Segmented, Space, Spin, Typography } from 'antd'
import moment from 'moment'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { BalancesService } from '../api/services.gen'
import { BalanceHistoryPointDto } from '../api/types.gen'

type Granularity = 'day' | 'week' | 'month'

interface BrokerBalanceHistoryProps {
  broker: string
}

const lineColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7f7f', '#8dd1e1']

const BrokerBalanceHistory: FunctionComponent<BrokerBalanceHistoryProps> = (props) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [granularity, setGranularity] = useState<Granularity>('day')
  const [history, setHistory] = useState<BalanceHistoryPointDto[]>([])

  const fetchHistory = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await BalancesService.getBalancesHistory({
        granularity,
        broker: props.broker,
      })
      setHistory(response.history)
    } catch (e: any) {
      console.error('Failed to fetch balance history:', e)
      setError(e.message || 'Failed to load balance history')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHistory()
  }, [props.broker, granularity])

  // One chart point per date, one line per currency
  const currencies = Array.from(new Set(history.map(entry => entry.currency)))
  const dataMap = new Map<string, any>()
  history.forEach(entry => {
    if (!dataMap.has(entry.date)) {
      dataMap.set(entry.date, { date: entry.date })
    }
    dataMap.get(entry.date)![entry.currency] = entry.value
  })
  const chartData = Array.from(dataMap.values()).sort((a, b) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  const formatDate = (date: string) =>
    moment(date).format('YYYY-MM-DD')

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Segmented
          options={['day', 'week', 'month']}
          value={granularity}
          onChange={value => setGranularity(value as Granularity)}
        />
        {loading && <Spin />}
      </Space>
      {error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : chartData.length === 0 && !loading ? (
        <Typography.Text type="secondary" style={{ display: 'block' }}>No balance history available</Typography.Text>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={formatDate} />
            <YAxis domain={['auto', 'auto']} />
            <Tooltip labelFormatter={formatDate} />
            <Legend />
            {currencies.map((currency, index) => (
              <Line
                key={currency}
                type="monotone"
                dataKey={currency}
                stroke={lineColors[index % lineColors.length]}
                name={currency}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

export { BrokerBalanceHistory }
